import { Component, HostListener, Input } from "@angular/core";
import { CategoryGroup, Child, Image, TextConfig } from "../../models/category-group.model";
import { ActivatedRoute } from "@angular/router";
import { Helper } from "src/app/core/helpers/helper";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ProductGroupComponent } from "../product-group/product-group.component";
import { MainCategory } from "../../models/main-category.model";

@Component({
  standalone: true,
  imports: [CommonModule, TranslateModule, ProductGroupComponent],
  selector: "app-category-group",
  templateUrl: "./category-group.component.html",
  styleUrl: "./category-group.component.scss"
})
export class CategoryGroupComponent {
  @Input() categoryGroups!: CategoryGroup[];
  @Input() merchantId: string = "";
  @Input() isCart!: boolean;
  @Input() serviceMainCategory!: MainCategory;
  @Input() serviceMerchantId: string = "";
  isDesktop: boolean = window.innerWidth >= 1024;

  constructor(public helper: Helper, private route: ActivatedRoute) {}

  @HostListener("window:resize", ["$event"])
  /**
   * Host listener for window resize events.
   * Updates the `isDesktop` property based on the window width.
   * Sets `isDesktop` to true if the window width is greater than or equal to 1024, otherwise false.
   */
  onResize(): void {
    this.isDesktop = window.innerWidth >= 1024;
  }

  /**
   * Returns the CSS styles for the main container of the category group.
   * The style includes background color, padding, border, and border radius,
   * which are determined by the main view configuration of the category group.
   * @param categoryGroup The category group whose styles are to be generated.
   * @returns An object containing the CSS styles for the main container.
   */
  getMainContainerStyle(categoryGroup: CategoryGroup): {
    [key: string]: string;
  } {
    const config = categoryGroup.mainViewConfig;
    return {
      backgroundColor: config.bgColor,
      padding: `${config.padding[0]}px ${config.padding[1]}px ${config.padding[2]}px ${config.padding[3]}px`,
      border: `${config.border.size}px solid ${config.border.color}`,
      borderRadius: `${config.border.radius[0]}px ${config.border.radius[1]}px ${config.border.radius[2]}px ${config.border.radius[3]}px`,
      margin: `${config.padding[0]}px ${config.padding[1]}px ${config.padding[2]}px ${config.padding[3]}px`
    };
  }

  /**
   * Returns the CSS styles for the category container.
   * The style includes display, gap, flex direction, margin, background color,
   * border, border radius, and padding based on the grid view configuration.
   *
   * @param categoryGroup The category group for which to generate the styles.
   * @returns An object with CSS styles for the category container.
   */
  getCategoryContainerStyle(categoryGroup: CategoryGroup): { [key: string]: string } {
    const grid = categoryGroup.gridViewConfig;
    const styles: { [key: string]: string } = {
      display: "flex",
      gap: `${grid.betweenPadding}px`,
      flexDirection: "column",
      margin: `${grid.padding[0]}px ${grid.padding[1]}px ${grid.padding[2]}px ${grid.padding[3]}px`,
      backgroundColor: grid.bgColor,
      border: `${grid.border.size}px solid ${grid.border.color}`,
      borderRadius: `${grid.border.radius[0]}px ${grid.border.radius[1]}px ${grid.border.radius[2]}px ${grid.border.radius[3]}px`,
      padding: `${grid.childPadding[0]}px ${grid.childPadding[1]}px ${grid.childPadding[2]}px ${grid.childPadding[3]}px`
    };
    return styles;
  }

  /**
   * Returns the style object for the grid layout of the categories.
   * The style object contains the display type (grid), gap between items, and
   * the grid template columns based on whether it is the first row or not.
   * If it is the first row, the grid template columns is set to repeat the
   * remainder items, otherwise it is set to repeat the maxColumnCount items.
   * @param categoryGroup The category group for which to generate the styles.
   * @param isFirstRow Whether it is the first row or not.
   * @returns An object with CSS styles for the grid layout.
   */
  getCategoryGridLayoutStyle(categoryGroup: CategoryGroup, isFirstRow: boolean): { [key: string]: string } {
    const grid = categoryGroup.gridViewConfig;
    const maxColumnCount = grid.maxColumnCount;
    const totalItems = categoryGroup.childs.length;
    const remainder = totalItems % maxColumnCount;
    const styles: { [key: string]: string } = {
      display: "grid",
      gap: `${grid.betweenPadding}px`
    };
    if (isFirstRow) {
      // First row: Use remainder items
      styles["gridTemplateColumns"] = `repeat(${remainder}, 1fr)`;
    } else {
      // Remaining rows: Use maxColumnCount
      styles["gridTemplateColumns"] = `repeat(${maxColumnCount}, 1fr)`;
    }
    return styles;
  }

  /**
   * Returns the style object for the grid item.
   * The style includes flex box configuration, text alignment, background color, border,
   * border radius, and padding based on the image configuration in the grid child view
   * configuration of the category group. If the category group's child type is not
   * "CATEGORY", the background color, border, and border radius are set to an empty string.
   *
   * @param categoryGroup The category group containing the grid child view configuration.
   * @returns An object with CSS styles for the grid item
   */
  getGridItemStyle(categoryGroup: CategoryGroup): { [key: string]: string } {
    const childConfig = categoryGroup.gridChildViewConfig;
    return {
      display: "flex",
      flexDirection:
        // eslint-disable-next-line no-nested-ternary
        childConfig.title.position[1] === "BOTTOM"
          ? "column"
          // eslint-disable-next-line no-nested-ternary
          : childConfig.title.position[1] === "CENTER" && childConfig.title.position[2] === "RIGHT"
          ? "row"
          : childConfig.title.position[1] === "CENTER" && childConfig.title.position[2] === "LEFT"
          ? "row-reverse"
          : "column-reverse",
      alignItems: "center",
      justifyContent: "space-between",
      textAlign: "center",
      backgroundColor: categoryGroup.childType === "CATEGORY" ? childConfig.bgColor : "",
      borderRadius:
        categoryGroup.childType === "CATEGORY"
          ? `${childConfig.border.radius[0]}px ${childConfig.border.radius[1]}px ${childConfig.border.radius[2]}px ${childConfig.border.radius[3]}px`
          : "",
      border: categoryGroup.childType === "CATEGORY" ? `${childConfig.border.size}px solid ${childConfig.border.color}` : "",
      padding: `${childConfig.padding[0]}px ${childConfig.padding[1]}px ${childConfig.padding[2]}px ${childConfig.padding[3]}px`
    };
  }

  /**
   * Returns the style object for the grid item image.
   * The style includes padding, border, border radius, and background color
   * based on the image configuration in the grid child view configuration.
   *
   * @param categoryGroup The category group containing the grid child view configuration.
   * @returns An object with CSS styles for the grid item image.
   */
  getGridItemImageStyle(categoryGroup: CategoryGroup): { [key: string]: string | undefined } {
    const imageConfig = categoryGroup.gridChildViewConfig.image;
    return {
      padding: `${imageConfig.padding[0]}px ${imageConfig.padding[1]}px ${imageConfig.padding[2]}px ${imageConfig.padding[3]}px`,
      border: `${imageConfig.border.size}px solid ${imageConfig.border.color}`,
      borderRadius: `${imageConfig.border.radius[0]}px ${imageConfig.border.radius[1]}px ${imageConfig.border.radius[2]}px ${imageConfig.border.radius[3]}px`,
      backgroundColor: imageConfig.bgColor
    };
  }

  /**
   * Returns the style object for the title text.
   * The style includes color, font size, font weight, padding, and text alignment
   * based on the title configuration.
   * @param titleConfig The title configuration object.
   * @returns An object with CSS styles for the title text.
   */
  getTitleStyle(titleConfig: TextConfig): { [key: string]: string | number } {
    return {
      color: titleConfig.color,
      fontSize: `${titleConfig.fontSize}px`,
      fontWeight: titleConfig.fontWeight,
      padding: `${titleConfig.padding[0]}px ${titleConfig.padding[1]}px ${titleConfig.padding[2]}px ${titleConfig.padding[3]}px`,
      textAlign: titleConfig.position[2].toLowerCase()
    };
  }

  /**
   * Returns the style object for the image.
   * @param imageConfig The image configuration object.
   * @returns An object with CSS styles for the image including padding, border, border radius, and background color.
   */
  getImageStyle(imageConfig: Image): { [key: string]: string | undefined } {
    return {
      padding: `${imageConfig.padding[0]}px ${imageConfig.padding[1]}px ${imageConfig.padding[2]}px ${imageConfig.padding[3]}px`,
      border: `${imageConfig.border.size}px solid ${imageConfig.border.color}`,
      borderRadius: `${imageConfig.border.radius[0]}px ${imageConfig.border.radius[1]}px ${imageConfig.border.radius[2]}px ${imageConfig.border.radius[3]}px`,
      backgroundColor: imageConfig.bgColor
    };
  }

  /**
   * Returns the style object for the description text.
   * The style includes color, font size, font weight, padding, and text alignment
   * based on the description configuration.
   * @param descriptionConfig The description configuration object.
   * @returns An object with CSS styles for the description text.
   */
  getDescriptionStyle(descriptionConfig: TextConfig): {
    [key: string]: string | number;
  } {
    return {
      color: descriptionConfig.color,
      fontSize: `${descriptionConfig.fontSize}px`,
      fontWeight: descriptionConfig.fontWeight,
      padding: `${descriptionConfig.padding[0]}px ${descriptionConfig.padding[1]}px ${descriptionConfig.padding[2]}px ${descriptionConfig.padding[3]}px`,
      textAlign: descriptionConfig.position[2].toLowerCase()
    };
  }

  /**
   * Returns the style object for the header of the category group.
   * The style includes background color, padding, border, border radius, flex direction,
   * display, justification, and alignment based on the header configuration of the category group.
   * @param categoryGroup The category group for which to generate the header style.
   * @returns An object with CSS styles for the header of the category group.
   */
  getHeaderStyle(categoryGroup: CategoryGroup): { [key: string]: string } {
    const header = categoryGroup.headerFooterViewConfig.header;
    return {
      backgroundColor: header.bgColor,
      padding: `${header.padding[0]}px ${header.padding[1]}px ${header.padding[2]}px ${header.padding[3]}px`,
      border: `${header.border.size}px solid ${header.border.color}`,
      borderRadius: `${header.border.radius[0]}px ${header.border.radius[1]}px ${header.border.radius[2]}px ${header.border.radius[3]}px`,
      // eslint-disable-next-line no-nested-ternary
      flexDirection: header.fullImage.url ? "column" : header.leftImage.url ? "row" : header.rightImage.url ? "row-reverse" : "",
      display: "flex",
      justifyContent: header.fullImage.url ? "center" : "space-between",
      alignItems: "center"
    };
  }

  /**
   * Returns the CSS styles for the footer of the category group.
   * Styles include background color, padding, border, and border radius,
   * which are determined by the footer configuration in the headerFooterViewConfig.
   *
   * @param categoryGroup The category group whose footer styles are to be generated.
   * @returns An object containing the CSS styles for the footer.
   */
  getFooterStyle(categoryGroup: CategoryGroup): { [key: string]: string } {
    const footer = categoryGroup.headerFooterViewConfig.footer;
    return {
      backgroundColor: footer.bgColor,
      padding: `${footer.padding[0]}px ${footer.padding[1]}px ${footer.padding[2]}px ${footer.padding[3]}px`,
      border: `${footer.border.size}px solid ${footer.border.color}`,
      borderRadius: `${footer.border.radius[0]}px ${footer.border.radius[1]}px ${footer.border.radius[2]}px ${footer.border.radius[3]}px`
    };
  }

  /**
   * Navigate to the product list page, passing the child's id, name, type and view type as query parameters.
   * @param child The child to navigate to.
   */
  goToList(child: Child): void {
    this.route.queryParams.subscribe((param) => {
      if (param["merchantId"]) {
        this.merchantId = param["merchantId"];
      }
    });
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      this.helper.route.navigate(["/users/create-booking/quick-commerce/products"], {
        queryParams: { id: child._id, name: child.name, type: child.childType, view: child.childViewType, merchantId: this.merchantId }
      });
    } else if (this.merchantId && this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      this.helper.route.navigate(["/users/create-booking/e-delivery/product-list"], {
        queryParams: { id: child._id, name: child.name, type: child.childType, view: child.childViewType, merchantId: this.merchantId }
      });
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      this.helper.route.navigate(["/users/create-booking/services/product-list"], {
        queryParams: {
          id: child._id,
          name: child.name,
          type: child.childType,
          view: child.childViewType,
          serviceMainCategory: JSON.stringify(this.serviceMainCategory),
          merchantId: this.serviceMerchantId ? this.serviceMerchantId : ""
        }
      });
    }
  }

  /**
   * Navigates to the appropriate product list page to view all items in the given category group.
   * Updates the `merchantId` property based on query parameters if present.
   * If the current route does not include "/users/create-booking/e-delivery", navigates to the quick commerce products page.
   * Otherwise, if `merchantId` is available, navigates to the e-delivery product list page.
   * Passes the category group's ID, title, and merchant ID as query parameters.
   *
   * @param categoryGroup The category group to be viewed in the product list.
   */
  viewAll(categoryGroup: CategoryGroup): void {
    this.route.queryParams.subscribe((param) => {
      if (param["merchantId"]) {
        this.merchantId = param["merchantId"];
      }
    });
    const customConfig = {
      gridChildViewConfig: categoryGroup.gridChildViewConfig,
      gridViewConfig: categoryGroup.gridViewConfig,
      isSetCustomDesign: true
    };
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      this.helper.route.navigate(["/users/create-booking/quick-commerce/products"], {
        queryParams: {
          groupId: categoryGroup._id,
          name: categoryGroup.headerFooterViewConfig.header.title.text,
          merchantId: localStorage.getItem("merchantId"),
          customConfig: JSON.stringify(customConfig)
        }
      });
    } else if (this.serviceMainCategory && this.serviceMainCategory._id) {
      this.helper.route.navigate(["/users/create-booking/services/product-list"], {
        queryParams: {
          groupId: categoryGroup._id,
          name: categoryGroup.headerFooterViewConfig.header.title.text,
          serviceMainCategory: JSON.stringify(this.serviceMainCategory),
          customConfig: JSON.stringify(customConfig),
          merchantId: this.serviceMerchantId ? this.serviceMerchantId : ""
        }
      });
    } else if (this.merchantId) {
      this.helper.route.navigate(["/users/create-booking/e-delivery/product-list"], {
        queryParams: {
          groupId: categoryGroup._id,
          name: categoryGroup.headerFooterViewConfig.header.title.text,
          merchantId: this.merchantId,
          customConfig: JSON.stringify(customConfig)
        }
      });
    }
  }

  isBrandType(categoryGroup: CategoryGroup): boolean {
    return categoryGroup.childType === "BRAND";
  }
}
